import { LockClosedIcon as LockClosedOutlineIcon } from '@heroicons/react/24/outline'
import { LockClosedIcon } from '@heroicons/react/24/solid'
import { useFormik } from 'formik'
import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'

import { QUERY_KEYS } from 'constants/constants'
import {
  useNotificationContext,
  usePartnerContext,
  usePaymentContext
} from '../../../context'
import { useFeatureGate } from '../../../features/pricing/use-feature-gate'
import { classNames, toBase64 } from '../../../helpers'
import { localStorage } from '../../../helpers/local-storage'
import { useMutationAndUpdateQuery } from '../../../hooks/use-mutation-and-update-query'
import Alert from '../../alert'
import LockBadge from '../../pricing/lock-badge'
import SelectMenu from '../../select-menu'
import InfoCallout from '../../tailwind/InfoCallout'
import SettingsContainer from '../settings-container'

const i18n_delete_spaces = 'settings.automations.delete_spaces.'

const AUTOMATION_FEATURE = 'GDPR_AUTOMATION'
const AUTOMATION_SOURCE = 'settings_automations_delete'

// Optionen, bei denen die Automatisierung löscht. independent heißt aus.
const DELETING_OPTIONS = ['all', 'anonym']

/**
 * Was ein Klick auf eine Option tut. In legacy und preview wie bisher, ohne
 * das Produkt öffnet eine gesperrte Option die Paywall. In enforced geht der
 * Wechsel auf independent (aus) immer, weil die Option immer verfügbar ist.
 * Die gespeicherte Option bleibt in Nutzung wählbar, damit sich ein Wechsel
 * vor dem Speichern zurücknehmen lässt. Jede andere gesperrte Option öffnet
 * den Upgrade-Dialog.
 *
 * @returns {'select'|'paywall'|'dialog'}
 */
export const deleteOptionAction = ({ enforced, available, inUse, isSaved }) => {
  if (available) return 'select'
  if (!enforced) return 'paywall'
  return inUse && isSaved ? 'select' : 'dialog'
}

/**
 * Label einer Option. Die LockBadge ist ein Button und gehört nicht in das
 * label des Radios, sonst wird ihr aria-label Teil des Namens der Option.
 * Mit badge stehen label und Pille deshalb nebeneinander in einer Zeile.
 * Ohne badge (legacy, preview, im Paket) bleibt das label wie bisher.
 */
const OptionLabel = ({ htmlFor, badge, children }) => {
  const label = (
    <label
      htmlFor={htmlFor}
      className='flex items-center group-hover:cursor-pointer'
    >
      {children}
    </label>
  )

  if (!badge) return label

  return (
    <div className='flex items-center'>
      {label}
      {badge}
    </div>
  )
}

OptionLabel.propTypes = {
  htmlFor: PropTypes.string.isRequired,
  badge: PropTypes.node,
  children: PropTypes.node.isRequired
}

OptionLabel.defaultProps = { badge: null }

const DeleteSpaces = () => {
  const { t } = useTranslation()
  const { partner } = usePartnerContext()
  const { automations, showPaywall } = usePaymentContext()
  const { success } = useNotificationContext()

  const navigate = useNavigate()

  const delete_spaces = useMemo(() => {
    const { delete_spaces: ds } = JSON.parse(
      partner.app_settings?.automations || '{}'
    )

    return ds || 'independent'
  }, [partner])

  const automation_soft_delete_after_months = useMemo(() => {
    const { automation_soft_delete_after_months } = JSON.parse(
      partner.app_settings?.automations || '{}'
    )

    return automation_soft_delete_after_months || 6
  }, [partner])

  // Neue Lock-UX nur in enforced, in legacy und preview bleibt die Paywall
  const gate = useFeatureGate(AUTOMATION_FEATURE, {
    source: AUTOMATION_SOURCE,
    inUse: DELETING_OPTIONS.includes(delete_spaces)
  })

  const partnerMutation = useMutationAndUpdateQuery({
    queryKey: QUERY_KEYS.partnerById(partner.id),
    mutation: 'updatePartner'
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      delete_spaces,
      months: automation_soft_delete_after_months
    },
    validationSchema: Yup.object({
      delete_spaces: Yup.string(),
      months: Yup.number()
    }),
    onSubmit: ({ delete_spaces, months }, { setSubmitting }) => {
      setSubmitting(true)

      const app_settings = partner.app_settings || {}
      const automations = JSON.parse(app_settings.automations || '{}')

      automations.delete_spaces = {
        independent: undefined,
        all: 'all',
        anonym: 'anonym'
      }[delete_spaces]

      automations.automation_soft_delete_after_months = months

      const input = { id: partner.id }
      input.app_settings = {
        ...app_settings,
        automations: JSON.stringify(automations)
      }

      const onSuccess = () => {
        setSubmitting(false)
        success(t('settings.automations.success_message'))
      }

      partnerMutation.mutate(
        { input },
        { onSuccess, onError: () => setSubmitting(false) }
      )
    }
  })

  const delete_space_settings = [
    {
      id: 'independent',
      name: t(i18n_delete_spaces + 'independent_label'),
      description: t(i18n_delete_spaces + 'independent_description'),
      available: true,
      link: {
        onClick: () => {
          const location = {
            pathname: '/talents',
            search: `computed=${toBase64([
              { more: { in: ['BEFORE_6_MONTH'] } }
            ])}`
          }

          localStorage.setItem(location.pathname, location.search)
          navigate(location.pathname)
        },
        text: t(i18n_delete_spaces + 'independent_link_text')
      }
    },
    {
      id: 'all',
      name: t(i18n_delete_spaces + 'all_label'),
      description: t(i18n_delete_spaces + 'all_description'),
      available: automations
    },
    {
      id: 'anonym',
      name: t(i18n_delete_spaces + 'anonym_label'),
      description: t(i18n_delete_spaces + 'anonym_description'),
      available: automations,
      recommended: true
    }
  ]

  // selectable ist in legacy und preview genau available. In enforced wirkt
  // die gespeicherte Option in Nutzung aktiv, nur ihr Schloss zeigt, dass sie
  // nicht im Paket ist.
  const delete_space_options = delete_space_settings.map((setting) => {
    const action = deleteOptionAction({
      enforced: gate.enforced,
      available: setting.available,
      inUse: gate.readOnly,
      isSaved: setting.id === delete_spaces
    })
    return { ...setting, action, selectable: action === 'select' }
  })

  const set_months_available =
    automations && ['all', 'anonym'].includes(formik.values.delete_spaces)
  // In enforced und in Nutzung bleiben die Monate lesbar, ändern lassen sie
  // sich aber nicht
  const months_read_only =
    gate.readOnly && DELETING_OPTIONS.includes(formik.values.delete_spaces)

  return (
    <SettingsContainer
      title={t(i18n_delete_spaces + 'settings_container_title')}
      description={t(i18n_delete_spaces + 'settings_container_description')}
      onSubmit={formik.handleSubmit}
      dirty={formik.dirty}
      isLoading={formik.isSubmitting}
    >
      <div className='space-y-5'>
        {gate.readOnly && (
          <InfoCallout tone='neutral' icon={LockClosedOutlineIcon}>
            {t('pricing.gates.automation_off_hint')}
          </InfoCallout>
        )}
        {delete_space_options.map(
          ({
            id,
            name,
            description,
            available,
            recommended,
            link,
            action,
            selectable
          }) => (
            <div
              key={id}
              onClick={() => {
                if (action === 'select')
                  formik.setFieldValue('delete_spaces', id)
                else if (action === 'dialog') gate.open()
                else showPaywall('general')
              }}
              className='group relative flex items-start'
            >
              <div
                className={classNames(
                  'flex h-6 items-center',
                  selectable ? '' : 'opacity-60'
                )}
              >
                <input
                  id={id}
                  aria-describedby={`${id}-description`}
                  name='plan'
                  type='radio'
                  checked={formik.values.delete_spaces === id}
                  disabled={!selectable}
                  readOnly
                  className='h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-600 group-hover:cursor-pointer'
                />
              </div>
              <div className='ml-3 mt-0.5 text-sm group-hover:cursor-pointer'>
                <OptionLabel
                  htmlFor={id}
                  badge={
                    !available && gate.enforced ? (
                      <LockBadge
                        feature={AUTOMATION_FEATURE}
                        source={AUTOMATION_SOURCE}
                        inUse={gate.readOnly}
                        className='ml-2'
                      />
                    ) : null
                  }
                >
                  <span
                    className={classNames(
                      'font-medium text-gray-900',
                      selectable ? '' : 'opacity-60'
                    )}
                  >
                    {name}
                  </span>
                  {recommended && (
                    <span className='ml-1 inline-flex items-center rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10'>
                      {t('settings.automations.recommended_badge')}
                    </span>
                  )}
                  {!available && !gate.enforced && (
                    <LockClosedIcon className='ml-1 h-5 w-5 text-aivyOrange' />
                  )}
                </OptionLabel>
                <p
                  id={`${id}-description`}
                  className={classNames(
                    'mt-0.5 max-w-lg text-gray-500 group-hover:cursor-pointer',
                    selectable ? '' : 'opacity-80'
                  )}
                >
                  {description}
                </p>
                {link && (
                  <div
                    onClick={link.onClick}
                    className='mt-1 text-sm font-semibold leading-6 text-blue-600 hover:text-blue-500'
                  >
                    {link.text}
                    <span aria-hidden='true'> &rarr;</span>
                  </div>
                )}
              </div>
            </div>
          )
        )}
        <div
          className={classNames(
            set_months_available || months_read_only ? '' : 'opacity-40'
          )}
        >
          <SelectMenu
            id='month'
            // label={t(i18n_delete_spaces + 'months_title')}
            label={t(i18n_delete_spaces + 'months_description')}
            options={[
              ...Array.from({ length: 12 }, (_, index) => index + 1).map(
                (months) => [
                  months,
                  t(
                    `${i18n_delete_spaces}months_${
                      months === 1 ? 'month' : 'months'
                    }`,
                    { number: months }
                  )
                ]
              )
            ]}
            defaultValue={formik.values.months}
            onChange={(months) => formik.setFieldValue('months', months)}
            className='w-full sm:w-2/3'
            translate={false}
            disabled={!set_months_available}
          />
        </div>
        {formik.dirty && (
          <div className='max-w-lg'>
            <Alert
              type='info'
              title={t('settings.automations.info_title')}
              description={t('settings.automations.info_description')}
            />
          </div>
        )}
      </div>
    </SettingsContainer>
  )
}

export default DeleteSpaces
